import { ApplicationConfig, ErrorHandler } from '@angular/core';
import { provideRouter } from '@angular/router';
import { routes } from './app.routes';
// import { provideClientHydration } from '@angular/platform-browser'; // Hydration removed - no SSR configured
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideZonelessChangeDetection } from '@angular/core';
import { providePrimeNG } from 'primeng/config';
import { MessageService } from 'primeng/api';
import Aura from '@primeuix/themes/aura';
import { definePreset } from '@primeuix/themes';
import { provideTanStackQuery } from '@tanstack/angular-query-experimental';
import { QueryClient } from '@tanstack/angular-query-experimental';
import { authInterceptor } from './auth/interceptors/auth.interceptor';
import { credentialsInterceptor } from './interceptors/credentials.interceptor';
import { loadingInterceptor } from './interceptors/loading.interceptor';
import { errorInterceptor } from './interceptors/error.interceptor';
import { shouldRetryQuery } from './shared/utils/query.utils';
import { AuthService, ConcreteAuthService } from './auth/services/auth.service';
import { DynatraceErrorHandler } from './shared/dynatrace';

const BayshorePreset = definePreset(Aura, {
  semantic: {
    primary: {
      50:  '#e8f3f7',
      100: '#c4e2ec',
      200: '#9dcee0',
      300: '#72b4d1',
      400: '#4e9fc4',
      500: '#2d8ab6',
      600: '#1c6f94',
      700: '#115472',
      800: '#0b435b',
      900: '#082e3f',
      950: '#041b25',
    },
  },
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideRouter(routes),
    // provideClientHydration(), // Hydration removed - no SSR configured
    provideAnimations(),
    provideTanStackQuery(new QueryClient({
      defaultOptions: {
        queries: {
          // Disable automatic refetching on window focus globally
          // This prevents the loading spinner from showing when user returns to the tab
          // Notification queries will explicitly enable this behavior with SKIP_LOADING
          refetchOnWindowFocus: false,
          refetchOnReconnect: false,
          // Don't retry 400/403/404/409/410/422 — the answer won't change and
          // each retry used to raise another error toast.
          retry: shouldRetryQuery,
        },
      },
    })),
    providePrimeNG({
      theme: {
        preset: BayshorePreset,
        options: {
          darkModeSelector: false || 'none',
          // PrimeNG theme options
          ripple: true,
          inputStyle: 'filled',
        },
      },
    }),
    provideHttpClient(withInterceptors([credentialsInterceptor, loadingInterceptor, authInterceptor, errorInterceptor])),
    MessageService,
    { provide: AuthService, useClass: ConcreteAuthService },
    { provide: ErrorHandler, useClass: DynatraceErrorHandler },
  ],
};
